import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {createBoundedAxisTooltip, formatCountAxis} from "../../helpers/chart_common";
import {formatTimeRange} from "../../helpers/tooltips";
import {debugLog} from '../../helpers/debug_log';

export class ShaperActiveCircuits extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.explainIssues = null;
    }

    title() {
        return "Shaper Active Circuit Count";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper Active Circuits</h5><p>Shows the number of circuits with non-zero traffic numbers by time.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Shaper Active Circuit Count", issues: this.explainIssues };
    }

    buildContainer() {
        let base = super.buildContainer();
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperActiveCircuitsGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "ShaperDetail" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [key, value] of Object.entries(msg.Data.blob)) {
            this.graph.update(value);
            this.lastGraphState = value;
            this.explainIssues = buildActiveCircuitIssues(value);
        }
    }

}

function buildActiveCircuitIssues(data) {
    const series = Array.isArray(data?.body?.active_circuits) ? data.body.active_circuits : [];
    const ticks = Array.isArray(data?.body?.ticks) ? data.body.ticks : [];
    if (series.length < 2 || ticks.length < 2) {
        return [];
    }

    let lastIdx = -1;
    for (let i = series.length - 1; i >= 0; i -= 1) {
        if (Number.isFinite(series[i]) && Number.isFinite(ticks[i])) {
            lastIdx = i;
            break;
        }
    }
    if (lastIdx <= 0) return [];

    let prevIdx = -1;
    for (let i = lastIdx - 1; i >= 0; i -= 1) {
        if (Number.isFinite(series[i]) && Number.isFinite(ticks[i])) {
            prevIdx = i;
            break;
        }
    }
    if (prevIdx < 0) return [];

    const prevVal = Number(series[prevIdx]);
    const lastVal = Number(series[lastIdx]);
    if (!Number.isFinite(prevVal) || !Number.isFinite(lastVal) || prevVal <= 0) {
        return [];
    }

    const change = (lastVal - prevVal) / prevVal;
    const absChange = Math.abs(change);
    if (absChange < 0.10) {
        return [];
    }

    const severity = absChange >= 0.25 ? "red" : "orange";
    const direction = change >= 0 ? "increased" : "decreased";
    const percentText = `${change >= 0 ? "+" : "-"}${(absChange * 100).toFixed(0)}%`;
    const timeRange = formatTimeRange(ticks[prevIdx], ticks[lastIdx]);

    return [
        {
            time_range: timeRange,
            severity,
            issue: `Active circuits ${direction}`,
            details: `Change ${percentText}`,
        },
    ];
}

class ShaperActiveCircuitsGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("Active Circuits", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option.yAxis.axisLabel.formatter = formatCountAxis;
        this.option.tooltip = createBoundedAxisTooltip({
            yFormatter: formatCountAxis,
            formatter: (params) => {
                if (!params || !params[0]) return "";
                return `<div style="font-size: 10px;">
${new Date(parseInt(params[0].axisValue, 10) * 1000).toLocaleString()}<br/>
<strong>Active circuits:</strong> ${Number(params[0].value || 0).toLocaleString()}
</div>`;
            },
        });
        
        // Add axis pointer configuration for highlighting
        this.option.axisPointer = {
            link: {xAxisIndex: 'all'},
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };
        
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();

        this.option.series = [ { name: "Active Circuits", type: "line", data: [] } ];
        this.option.xAxis.data = [];
        this.option.legend.data = [];

        debugLog(data);

        // Add series for each shaper
        let n = 0;

        for (let x=0; x<data.body.active_circuits.length; x++) {
            this.option.xAxis.data.push(data.body.ticks[x]);
            this.option.series[0].data.push(data.body.active_circuits[x]);
        }

        n++;

        // Apply
        this.chart.setOption(this.option, true);
    }
}
